import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Lock, Sparkles, Check } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import axios from 'axios';
import { toast } from 'sonner';
import TemplatePreview from '@/components/studio/TemplatePreview';
import { TEMPLATES } from '@/lib/store';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// `plan` is "studio" ($9), "freehand" ($29.99), or "bundle" ($34.99). Copy,
// price and entitlement check all switch off it; the checkout flow itself is
// identical.
const PLAN_META = {
  studio: {
    title: 'Unlock Panels Studio Pro',
    price: 9,
    priceLabel: '$9',
    button: (u) => (u ? 'Upgrade for $9' : 'Sign in to upgrade'),
    features: [
      'Export STL, OBJ & PNG',
      'Panel Sheet SVG for real leather cutting',
      'Save unlimited designs to your gallery',
      'All future export formats included',
    ],
  },
  freehand: {
    title: 'Unlock Freehand Draw Pro',
    price: 29.99,
    priceLabel: '$29.99',
    button: (u) => (u ? 'Upgrade for $29.99' : 'Sign in to upgrade'),
    features: [
      'Freehand, polyline & shape-stamp drawing on the ball',
      'Symmetry modes: Mirror X/Y/Z, Antipodal, Ring',
      'Punch-through holes with matching stitch counts',
      'Move / scale / rotate / duplicate any panel',
      'Grid & edge snapping · reference photo tracing',
      'SVG/DXF flat-pattern exports with holes',
    ],
  },
  bundle: {
    title: 'Unlock Studio + Freehand',
    price: 34.99,
    priceLabel: '$34.99',
    button: (u) => (u ? 'Get the bundle · $34.99' : 'Sign in to upgrade'),
    features: [
      'Everything in Panels Studio Pro',
      'Everything in Freehand Draw Pro',
      'Save $4 vs. buying each plan on its own',
      'One checkout — both entitlements unlocked forever',
    ],
  },
};

export default function UpgradeDialog({ open, onOpenChange, previewTemplateId, plan: initialPlan = 'studio', hideBundle = false }) {
  const { user, login, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(initialPlan);
  useEffect(() => { setPlan(initialPlan); }, [initialPlan, open]);
  const meta = PLAN_META[plan] || PLAN_META.studio;
  const previewMeta = previewTemplateId ? TEMPLATES.find((t) => t.id === previewTemplateId) : null;
  // Only offer the bundle upsell from the single-plan dialogs — not when the
  // user already opened the bundle view.
  const showBundleUpsell = plan !== 'bundle';

  const handleUpgrade = async () => {
    if (!user) { login(); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        origin_url: window.location.origin,
        plan,
      });
      if (data.already_pro) { await refresh(); onOpenChange(false); return; }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error('Could not start checkout');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="upgrade-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl tracking-tight flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#007AFF]" /> {meta.title}
          </DialogTitle>
          <DialogDescription>One-time <span className="font-semibold text-slate-900">{meta.priceLabel}</span> · yours forever.</DialogDescription>
        </DialogHeader>
        {previewMeta && plan !== 'bundle' && (
          <div className="pt-1 pb-1" data-testid="upgrade-preview-block">
            <TemplatePreview templateId={previewMeta.id} size={180} />
            <div className="text-center mt-2">
              <div className="text-sm font-semibold text-slate-900">{previewMeta.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{previewMeta.description}</div>
            </div>
          </div>
        )}
        <ul className="space-y-2 my-3 text-sm text-slate-700">
          {meta.features.map((f) => (
            <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-500 mt-0.5" /> {f}</li>
          ))}
        </ul>
        <Button
          onClick={handleUpgrade}
          disabled={loading}
          className="w-full rounded-full h-12 bg-[#007AFF] hover:bg-[#0062CC] text-white text-base"
          data-testid="btn-upgrade-checkout"
        >
          {loading ? 'Redirecting…' : meta.button(user)}
        </Button>
        {!hideBundle && (showBundleUpsell ? (
          <button
            type="button"
            onClick={() => setPlan('bundle')}
            className="mt-2 w-full text-center text-xs text-slate-600 hover:text-[#007AFF] rounded-lg py-2 border border-dashed border-slate-200 hover:border-[#007AFF]/40 transition-colors"
            data-testid="btn-switch-bundle"
          >
            Or grab <span className="font-semibold">Studio + Freehand</span> for $34.99 · save $4
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setPlan(initialPlan)}
            className="mt-2 w-full text-center text-xs text-slate-500 hover:text-slate-800 rounded-lg py-2"
            data-testid="btn-switch-single"
          >
            ← Back to the {PLAN_META[initialPlan]?.priceLabel} single plan
          </button>
        ))}
      </DialogContent>
    </Dialog>
  );
}

export function PaymentSuccess() {
  const { refresh } = useAuth();
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (!sid) { setStatus('missing'); return; }
    let cancelled = false;
    let tries = 0;
    const tick = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sid}`);
        if (data.payment_status === 'paid') {
          await refresh();
          setStatus('paid');
          setTimeout(() => (window.location.href = '/'), 1500);
          return;
        }
      } catch (err) {
        // 404 briefly after redirect is expected; log others for debugging
        if (err?.response?.status !== 404) console.debug('payment status poll failed', err);
      }
      if (tries < 20) setTimeout(tick, 2000);
      else setStatus('timeout');
    };
    tick();
    return () => { cancelled = true; };
  }, [refresh]);

  return (
    <div className="flex items-center justify-center h-screen bg-[#F8F9FA]" data-testid="payment-success">
      <div className="text-center px-6">
        {status === 'paid' ? (
          <>
            <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
              <Check className="w-7 h-7 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900">You're Pro!</h1>
            <p className="text-slate-500 mt-1">Redirecting you back to the studio…</p>
          </>
        ) : status === 'timeout' ? (
          <p className="text-slate-600">Payment still processing. Refresh in a moment.</p>
        ) : (
          <p className="text-slate-600">Confirming your payment…</p>
        )}
      </div>
    </div>
  );
}
